import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import { PURCHASE_API } from "../../apiConfig";
import { authHeaders } from "../../utils/authHeaders";
import Select from "react-select";
import { FileText } from 'lucide-react';
import { MASTER_API } from "../../apiConfig";


const styles = {
  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  erpTable: {
    width: "100%",
    minWidth: "600px",
    tableLayout: "fixed",
    borderCollapse: "collapse"
  },
  erpTh: {
    background: "#f8fafc",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
    whiteSpace: "nowrap"
  },
  erpTd: {
    fontSize: 13,
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155",
    verticalAlign: "middle"
  },
  filterCard: {
    background: '#ffffff',
    borderRadius: 8,
    padding: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    marginBottom: 18
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    color: '#334155'
  },
  pageTitleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: 2
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#64748b'
  },
  transferBtn: {
    background: '#090909',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '9px 20px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(37,99,235,0.35)',
    transition: 'background 0.15s ease'
  },
  transferBtnDisabled: {
    background: '#cbd5e1',
    boxShadow: 'none',
    cursor: 'not-allowed'
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    background: '#fff7ed',
    border: '1px solid #fdba74',
    color: '#9a3412',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 14
  },
  warningIcon: {
    fontSize: 15,
    lineHeight: '18px'
  },
  warningCloseBtn: {
    marginLeft: 'auto',
    border: 'none',
    background: 'transparent',
    color: '#9a3412',
    fontSize: 16,
    lineHeight: 1,
    cursor: 'pointer',
    padding: 0
  },
  requiredMark: {
    color: '#dc2626'
  },
  fieldErrorText: {
    fontSize: 12,
    color: '#dc2626',
    marginTop: 4
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modalBox: {
    background: '#fff',
    borderRadius: 10,
    width: '480px',
    maxWidth: '92vw',
    padding: 24,
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1e293b'
  },
  modalCloseBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    color: '#64748b'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 22
  },
  modalCancelBtn: {
    background: '#f1f5f9',
    color: '#334155',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer'
  },
  modalSubmitBtn: {
    background: '#050505',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer'
  },
};

const rsFilter = {
  control: (base) => ({
    ...base,
    height: "36px",
    minHeight: "36px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "36px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px", position: "absolute" }),
  menuPortal: (base) => ({ ...base, zIndex: 100000 }),
};

const USER_MANUAL_SCOPE = {
    moduleName: "Logistics",
    process: "Inbound",
    subProcess: "Put Away"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;


function Putaway() {
  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [putawayList, setPutawayList] = useState([]);
  const [activeTab, setActiveTab] = useState("active"); // "active" | "posted"

  const [productCodes, setProductCodes] = useState([]);
  const [grnNumbers, setGrnNumbers] = useState([]);
  const [batchNumbers, setBatchNumbers] = useState([]);

  const [filters, setFilters] = useState({
    productCode: "",
    productCodeLabel: "",
    grnNumber: "",
    batchNumber: ""
  });

  // ── Row selection (checkboxes) ──
  // Only rows sharing the same status as the first-selected row can be
  // selected together, since a single Transfer action should apply to a
  // single, consistent status group.
  // QC-pending rows (stock.status) can never be put away.
  const isQcPending = (row) =>
    String(row.status || "").trim().toLowerCase() === "available for quality";

  const [selectedBatches, setSelectedBatches] = useState([]);
  const [warningMsg, setWarningMsg] = useState("");
  const selectedStatus =
    selectedBatches.length > 0
      ? (putawayList.find((r) => r.batch_number === selectedBatches[0])?.status || "")
      : null;

  const showWarning = (msg) => {
    setWarningMsg(msg);
    window.clearTimeout(showWarning._t);
    showWarning._t = window.setTimeout(() => setWarningMsg(""), 5000);
  };

  const toggleRow = (row) => {
    if (isQcPending(row)) {
      showWarning("QC is not cleared for this batch. It cannot be put away yet.");
      return;
    }
    const key = row.batch_number;
    setSelectedBatches((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      // If something is already selected, only allow adding rows with the
      // same status — otherwise warn and leave the selection unchanged.
      if (prev.length > 0 && selectedStatus !== (row.status || "")) {
        showWarning("You cannot select rows with a different status in the same transfer. Please select rows with the same status.");
        return prev;
      }
      return [...prev, key];
    });
  };

  const isRowSelectable = (row) =>
    !isQcPending(row) &&
    (selectedBatches.length === 0 || (row.status || "") === selectedStatus);

  const allSelectableKeys = putawayList
    .filter((r) => !isQcPending(r))
    .filter((r) => selectedBatches.length === 0 || (r.status || "") === selectedStatus)
    .map((r) => r.batch_number);

  const allSelected =
    allSelectableKeys.length > 0 &&
    allSelectableKeys.every((k) => selectedBatches.includes(k));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedBatches([]);
      return;
    }
    const eligibleRows = putawayList.filter((r) => !isQcPending(r));
    const distinctStatuses = [...new Set(eligibleRows.map((r) => r.status || ""))];
    if (distinctStatuses.length > 1) {
      showWarning("You cannot select all — the rows on this page have different statuses. Please select rows of the same status individually.");
      setSelectedBatches([]);
      return;
    }
    setSelectedBatches(eligibleRows.map((r) => r.batch_number));
  };

  // ── Transfer modal ──
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [storageLocations, setStorageLocations] = useState([]);
  const [transferForm, setTransferForm] = useState({
    storageLocation: null, // { value: "location|code", label }
    storageType: null,     // blank dropdown for now
    storageBin: null       // blank dropdown for now
  });
  const [storageLocationError, setStorageLocationError] = useState("");
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${PURCHASE_API}/goods-receipt/grn-storage-locations`, { headers: authHeaders() })
      .then((res) => setStorageLocations(res.data || []))
      .catch((err) => console.error("STORAGE LOCATIONS ERROR:", err));
  }, []);

  const openTransferModal = () => {
    setTransferForm({ storageLocation: null, storageType: null, storageBin: null });
    setStorageLocationError("");
    setShowTransferModal(true);
  };

  const closeTransferModal = () => setShowTransferModal(false);

  const handleTransferSubmit = async () => {
    // Storage Location is mandatory.
    if (!transferForm.storageLocation) {
      setStorageLocationError("Storage Location is required.");
      return;
    }
    if (selectedBatches.length === 0) {
      showWarning("Select at least one batch before transferring.");
      return;
    }

    const [storageLocation, storageCode] = (transferForm.storageLocation?.value || "|").split("|");

    const payload = {
      batchNumbers: selectedBatches,
      storageLocation,
      storageCode,
      storageType: transferForm.storageType?.value || "",
      storageBin: transferForm.storageBin?.value || ""
    };

    setTransferSubmitting(true);
    try {
      await axios.post(`${PURCHASE_API}/putaway/transfer`, payload, { headers: authHeaders() });

      setShowTransferModal(false);
      setSelectedBatches([]);

      // Refetch the list so the transferred batches reflect their new
      // storage location/code and drop out of "Active" into "Posted".
      const res = await axios.get(`${PURCHASE_API}/putaway/list`, {
        headers: authHeaders(),
        params: { ...filters, tab: activeTab }
      });
      setPutawayList(res.data || []);
    } catch (err) {
      console.error("PUTAWAY TRANSFER ERROR:", err);
      const msg = err?.response?.data?.message || "Transfer failed. Please try again.";
      setStorageLocationError(msg);
    } finally {
      setTransferSubmitting(false);
    }
  };

  // Filter dropdown source lists
  useEffect(() => {
    axios.get(`${PURCHASE_API}/putaway/filters`, { headers: authHeaders() })
      .then((res) => {
        setProductCodes(res.data?.productCodes || []);
        setGrnNumbers(res.data?.grnNumbers || []);
        setBatchNumbers(res.data?.batchNumbers || []);
      })
      .catch((err) => console.error("PUTAWAY FILTERS ERROR:", err));
  }, []);

  // List, refetched whenever filters or the tab change
  useEffect(() => {
    axios.get(`${PURCHASE_API}/putaway/list`, {
      headers: authHeaders(),
      params: { ...filters, tab: activeTab }
    })
      .then((res) => {
        setPutawayList(res.data || []);
        setSelectedBatches([]); // list changed — selection no longer valid
        setWarningMsg("");
      })
      .catch((err) => console.error("PUTAWAY LIST ERROR:", err));
  }, [filters, activeTab]);

  return (
    <Layout>
      <style>{`
        .pw-status-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
        }
        .pw-status-instock   { background: #dcfce7; color: #166534; }
        .pw-status-pending   { background: #fef9c3; color: #854d0e; }
        .pw-status-rejected  { background: #fee2e2; color: #991b1b; }
        .pw-status-default   { background: #f1f5f9; color: #475569; }
        .pw-row-hover:hover  { background: #f8fafc; }
        .pw-tab-toggle {
          display: inline-flex;
          background: #f1f5f9;
          border-radius: 8px;
          padding: 3px;
          gap: 2px;
          margin-bottom: 14px;
        }
        .pw-tab-toggle button {
          border: none;
          background: transparent;
          border-radius: 6px;
          padding: 5px 20px;
          font-size: 13px;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .pw-tab-toggle button.active-tab {
          background: #fff;
          color: #1e293b;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
      `}</style>

      {/* ===================== HEADER ===================== */}
      <div style={styles.pageTitleBar}>
        <div>
          <h2 style={styles.pageTitle}>Putaway</h2>
          <div style={styles.pageSubtitle}>View received batches ready for putaway</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {activeTab !== "posted" && (
          <button
            style={{
              ...styles.transferBtn,
              ...(selectedBatches.length === 0 ? styles.transferBtnDisabled : {})
            }}
            disabled={selectedBatches.length === 0}
            onClick={openTransferModal}
          >
            Transfer
          </button>
        )}

        <button
    onClick={() => {
    setManualExists(null);
    setManualFile(null);
    setShowManual(true);
    fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
        .then((res) => res.json())
        .then((data) => {
            const norm = (v) => String(v || "").trim().toLowerCase();
            const match = (data?.data || []).find((m) =>
                norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
            );
            if (match && match.drive_link) {
                setManualFile(match);
                setManualExists(true);
            } else {
                setManualExists(false);
            }
        })
        .catch(() => setManualExists(false));
}}
    title="View User Manual"
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      padding: "10px 14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      color: "#3b82f6",
      fontSize: "14px",
      fontWeight: "600",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
  >
    <FileText size={18} />
    User Manual
  </button>
      </div>
      </div>

      {showManual && (
  <div
    onClick={() => setShowManual(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100,
    }}
  >
    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
      }}>
        <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
          User Manual
        </h4>
        <button
          onClick={() => setShowManual(false)}
          style={{
            border: "none", background: "#f1f5f9", borderRadius: "8px",
            width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
            color: "#475569", lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {manualExists === null && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
          Checking for user manual...
        </div>
      )}

      {manualExists === false && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
        }}>
          <FileText size={40} color="#cbd5e1" />
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
            User Manual Not Available
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
            The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
          </div>
        </div>
      )}

      {manualExists === true && manualFile && (() => {
    const MANUAL_SCALE = 1.25;
    return (
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
            <iframe
                src={manualFile.drive_link.replace("/view", "/preview")}
                title={manualFile.file_name || "User Manual"}
                allow="autoplay"
                style={{
                    border: "none",
                    width: `${100 / MANUAL_SCALE}%`,
                    height: `${100 / MANUAL_SCALE}%`,
                    transform: `scale(${MANUAL_SCALE})`,
                    transformOrigin: "top left",
                }}
            />
        </div>
    );
})()}
    </div>
  </div>
)}

      {/* ===================== FILTERS ===================== */}
      <div style={styles.filterCard} className="pw-filter-card">
        <div className="row g-3">
          <div className="col-md-4 col-sm-6">
            <label style={styles.filterLabel}>Product Code</label>
            <Select
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={productCodes.map((p) => ({
                value: p.productCode,
                label: p.productDescription ? `${p.productCode} — ${p.productDescription}` : p.productCode
              }))}
              value={filters.productCode ? { value: filters.productCode, label: filters.productCodeLabel || filters.productCode } : null}
              onChange={(opt) => setFilters((prev) => ({ ...prev, productCode: opt?.value || "", productCodeLabel: opt?.label || "" }))}
            />
          </div>
          <div className="col-md-4 col-sm-6">
            <label style={styles.filterLabel}>GRN Number</label>
            <Select
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={grnNumbers.map((v) => ({ value: v, label: v }))}
              value={filters.grnNumber ? { value: filters.grnNumber, label: filters.grnNumber } : null}
              onChange={(opt) => setFilters((prev) => ({ ...prev, grnNumber: opt?.value || "" }))}
            />
          </div>
          <div className="col-md-4 col-sm-6">
            <label style={styles.filterLabel}>Batch Number</label>
            <Select
              styles={rsFilter}
              isClearable
              placeholder="Select"
              options={batchNumbers.map((v) => ({ value: v, label: v }))}
              value={filters.batchNumber ? { value: filters.batchNumber, label: filters.batchNumber } : null}
              onChange={(opt) => setFilters((prev) => ({ ...prev, batchNumber: opt?.value || "" }))}
            />
          </div>
        </div>
      </div>

      {/* ===================== ACTIVE / POSTED TOGGLE ===================== */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
        <div className="pw-tab-toggle" style={{ marginBottom: 0 }}>
          <button className={activeTab === "active" ? "active-tab" : ""} onClick={() => setActiveTab("active")}>
            Active
          </button>
          <button className={activeTab === "posted" ? "active-tab" : ""} onClick={() => setActiveTab("posted")}>
            Posted
          </button>
        </div>
      </div>

      {/* ===================== SELECTION WARNING ===================== */}
      {warningMsg && (
        <div style={styles.warningBanner}>
          <span style={styles.warningIcon}>⚠️</span>
          <span>{warningMsg}</span>
          <button style={styles.warningCloseBtn} onClick={() => setWarningMsg("")}>&times;</button>
        </div>
      )}

      {/* ===================== TABLE ===================== */}
      <div style={{ ...styles.tableContainer, overflowX: "auto" }} className="pw-table-wrapper">
        <table className="table mb-0" style={{ ...styles.erpTable, tableLayout: "auto", minWidth: 900 }}>
          <thead>
            <tr>
              {activeTab !== "posted" && (
                <th style={{ ...styles.erpTh, width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    disabled={putawayList.length === 0}
                  />
                </th>
              )}
              <th style={styles.erpTh}>Product Code</th>
              <th style={styles.erpTh}>Description</th>
              <th style={styles.erpTh}>Grn Number</th>
              <th style={styles.erpTh}>Batch Number</th>
              <th style={styles.erpTh}>Qty</th>
              <th style={styles.erpTh}>UOM</th>
              <th style={styles.erpTh}>Storage Location</th>
              <th style={styles.erpTh}>Storage Code</th>
              <th style={styles.erpTh}>Status</th>
            </tr>
          </thead>
          <tbody>
            {putawayList.length === 0 ? (
              <tr>
                <td colSpan={activeTab === "posted" ? 9 : 10} style={{ ...styles.erpTd, textAlign: "center", color: "#94a3b8", padding: "32px 0" }}>
                  No records found
                </td>
              </tr>
            ) : (
              putawayList.map((row, idx) => {
                const status = String(row.status || "").toLowerCase();
                const badgeClass =
                  status === "in stock" ? "pw-status-instock" :
                  status === "pending" ? "pw-status-pending" :
                  status === "rejected" ? "pw-status-rejected" :
                  "pw-status-default";
                const checked = selectedBatches.includes(row.batch_number);
                const selectable = isRowSelectable(row);
                return (
                  <tr key={row.batch_number || idx} className="pw-row-hover">
                    {activeTab !== "posted" && (
                      <td style={styles.erpTd}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!selectable}
                          onChange={() => toggleRow(row)}
                        />
                      </td>
                    )}
                    <td style={{ ...styles.erpTd, fontWeight: 600 }}>{row.product_code || "-"}</td>
                    <td style={styles.erpTd}>{row.description || "-"}</td>
                    <td style={styles.erpTd}>{row.grn_number || "-"}</td>
                    <td style={{ ...styles.erpTd, fontWeight: 600 }}>{row.batch_number || "-"}</td>
                    <td style={styles.erpTd}>{row.received_qty ?? "-"}</td>
                    <td style={styles.erpTd}>{row.uom || "-"}</td>
                    <td style={styles.erpTd}>{row.storage_location || "-"}</td>
                    <td style={styles.erpTd}>{row.storage_code || "-"}</td>
                    <td style={styles.erpTd}>
                      <span className={`pw-status-badge ${badgeClass}`}>
                        {row.status || "—"}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ===================== TRANSFER MODAL ===================== */}
      {showTransferModal && (
        <div style={styles.modalOverlay} onClick={closeTransferModal}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Transfer</div>
              <button style={styles.modalCloseBtn} onClick={closeTransferModal}>&times;</button>
            </div>

            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
              {selectedBatches.length} batch{selectedBatches.length === 1 ? "" : "es"} selected
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={styles.filterLabel}>
                Storage Location <span style={styles.requiredMark}>*</span>
              </label>
              <Select
                styles={{
                  ...rsFilter,
                  control: (base, state) => ({
                    ...rsFilter.control(base, state),
                    borderColor: storageLocationError ? "#dc2626" : base.borderColor,
                  })
                }}
                isClearable
                placeholder="Select"
                options={storageLocations.map((item) => ({
                  value: `${item.storage_location}|${item.storage_code}`,
                  label: `${item.storage_location} - ${item.storage_code}`
                }))}
                value={transferForm.storageLocation}
                onChange={(opt) => {
                  setTransferForm((prev) => ({ ...prev, storageLocation: opt }));
                  if (opt) setStorageLocationError("");
                }}
              />
              {storageLocationError && (
                <div style={styles.fieldErrorText}>{storageLocationError}</div>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={styles.filterLabel}>Storage Type</label>
              <Select
                styles={rsFilter}
                isClearable
                placeholder="Select"
                options={[]}
                value={transferForm.storageType}
                onChange={(opt) => setTransferForm((prev) => ({ ...prev, storageType: opt }))}
              />
            </div>

            <div style={{ marginBottom: 4 }}>
              <label style={styles.filterLabel}>Storage Bin</label>
              <Select
                styles={rsFilter}
                isClearable
                placeholder="Select"
                options={[]}
                value={transferForm.storageBin}
                onChange={(opt) => setTransferForm((prev) => ({ ...prev, storageBin: opt }))}
              />
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.modalCancelBtn} onClick={closeTransferModal} disabled={transferSubmitting}>Cancel</button>
              <button
                style={{
                  ...styles.modalSubmitBtn,
                  ...(transferSubmitting ? { opacity: 0.7, cursor: "not-allowed" } : {})
                }}
                onClick={handleTransferSubmit}
                disabled={transferSubmitting}
              >
                {transferSubmitting ? "Transferring..." : "Transfer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Putaway;